import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Stack,
	Switch,
	TextField,
	Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import adminQueueService from '../../../../../services/adminQueueService';

const EMPTY_FORM = {
	nameKk: '',
	nameRu: '',
	nameEn: '',
	description: '',
	icon: '',
	color: '#EDE9FE',
	prefix: '',
	isActive: true
};

function ServiceTypeModal({ open, onClose, onSuccess, editingItem }) {
	const { t } = useTranslation();
	const isEdit = !!editingItem;

	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setForm(
				editingItem
					? {
							nameKk: editingItem.nameKk || '',
							nameRu: editingItem.nameRu || '',
							nameEn: editingItem.nameEn || '',
							description: editingItem.description || '',
							icon: editingItem.icon || '',
							color: editingItem.color || '#EDE9FE',
							prefix: editingItem.prefix || '',
							isActive: editingItem.isActive !== false
					  }
					: EMPTY_FORM
			);
			setErrors({});
		}
	}, [open, editingItem]);

	const validate = () => {
		const e = {};
		if (!form.nameKk.trim()) e.nameKk = t('common.required');
		if (!form.nameRu.trim()) e.nameRu = t('common.required');
		if (!form.prefix.trim()) e.prefix = t('common.required');
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const handleChange = (field) => (e) => {
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
		setForm((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
	};

	const handleSubmit = async () => {
		if (!validate()) return;
		setLoading(true);
		try {
			if (isEdit) {
				await adminQueueService.updateServiceType(editingItem.id, form);
				toast.success(t('admin.serviceTypes.updatedSuccess'));
			} else {
				await adminQueueService.createServiceType(form);
				toast.success(t('admin.serviceTypes.createdSuccess'));
			}
			onSuccess();
		} catch (e) {
			const msg =
				e?.response?.data?.message ||
				e?.response?.data?.error ||
				t('common.errorSave');
			toast.error(msg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
			<DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
				{isEdit ? t('admin.serviceTypes.modalEditTitle') : t('admin.serviceTypes.modalAddTitle')}
			</DialogTitle>

			<DialogContent>
				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<TextField
						label={t('admin.serviceTypes.fieldNameKk')}
						fullWidth
						size='small'
						value={form.nameKk}
						onChange={handleChange('nameKk')}
						error={!!errors.nameKk}
						helperText={errors.nameKk}
					/>
					<TextField
						label={t('admin.serviceTypes.fieldNameRu')}
						fullWidth
						size='small'
						value={form.nameRu}
						onChange={handleChange('nameRu')}
						error={!!errors.nameRu}
						helperText={errors.nameRu}
					/>
					<TextField
						label={t('admin.serviceTypes.fieldNameEn')}
						fullWidth
						size='small'
						value={form.nameEn}
						onChange={handleChange('nameEn')}
					/>
					<TextField
						label={t('admin.serviceTypes.fieldDescription')}
						fullWidth
						size='small'
						multiline
						rows={2}
						value={form.description}
						onChange={handleChange('description')}
					/>
					<TextField
						label={t('admin.serviceTypes.fieldPrefix')}
						fullWidth
						size='small'
						value={form.prefix}
						onChange={e => setForm(prev => ({ ...prev, prefix: e.target.value.toUpperCase().slice(0, 5) }))}
						error={!!errors.prefix}
						helperText={errors.prefix || t('admin.serviceTypes.fieldPrefixHint')}
						inputProps={{ maxLength: 5 }}
					/>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Box>
							<Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
								{t('admin.serviceTypes.fieldColor')}
							</Typography>
							<Box
								component='input'
								type='color'
								value={form.color || '#EDE9FE'}
								onChange={handleChange('color')}
								sx={{
									width: 48,
									height: 36,
									border: '1px solid rgba(0,0,0,0.23)',
									borderRadius: 1,
									cursor: 'pointer',
									padding: '2px'
								}}
							/>
						</Box>
						<Box
							sx={{
								flex: 1,
								height: 36,
								borderRadius: 1,
								backgroundColor: form.color || '#EDE9FE',
								border: '1px solid rgba(0,0,0,0.12)'
							}}
						/>
					</Stack>
					<FormControlLabel
						control={
							<Switch
								checked={form.isActive}
								onChange={handleChange('isActive')}
								color='primary'
							/>
						}
						label={t('admin.serviceTypes.fieldIsActive')}
					/>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
				<Button
					onClick={onClose}
					disabled={loading}
					sx={{ textTransform: 'none', borderRadius: 2 }}
				>
					{t('common.cancel')}
				</Button>
				<Button
					variant='contained'
					onClick={handleSubmit}
					disabled={loading}
					sx={{ textTransform: 'none', borderRadius: 2, minWidth: 130 }}
				>
					{loading ? (
						<CircularProgress size={18} sx={{ color: 'inherit' }} />
					) : isEdit ? (
						t('common.save')
					) : (
						t('common.create')
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ServiceTypeModal;
